<template>
    <div>
        <div class="mb-4 flex items-center justify-between space-x-2">
            <h1 class="text-lg font-semibold text-gray-700">Users</h1>
            <div class="flex items-center space-x-2">
                <UInput 
                    v-model="search" 
                    type="text"
                    placeholder="Search..."
                    icon="i-ph-magnifying-glass-duotone"
                />
                <USelectMenu
                    v-model="filters.role"
                    trailing
                    :class="filters.role ? 'w-52' : 'w-40'"
                    :leading-icon="filters.role ? undefined : 'i-ph-shield-duotone'"
                    :ui="{
                        icon: { base: '!size-4', trailing: { pointer: 'pointer-events-auto', base: '!size-4' } }
                    }"
                    :options="[
                        { value: UserRole.admin, name: 'Administrator' },
                        { value: UserRole.medicalDirector, name: 'Medical Director' },
                        { value: UserRole.doctor, name: 'Doctor' },
                        { value: UserRole.operator, name: 'Operator' },
                        { value: UserRole.partner, name: 'Partner' },
                        { value: UserRole.viewer, name: 'Viewer' },
                        ...(auth.isEnabled('field_posts') ? [{ value: UserRole.fieldMedic, name: 'Field Medic' }] : []),
                    ]"
                >
                    <template #label>
                        <template v-if="filters.role">
                            <span class="text-gray-400">Role:</span>
                            <span class="truncate font-medium">{{ filters.role.name }}</span>
                        </template>
                        <span v-else class="text-gray-400">Role</span>
                    </template>
                    <template #option="{ option }">
                        <span :class="[option.dot, 'inline-block size-1.5 mx-1 flex-shrink-0 rounded-full']" aria-hidden="true" />
                        <span class="truncate">{{ option.name }}</span>
                    </template>
                    <template #trailing v-if="filters.role">
                        <button @click.stop="filters.role = undefined" type="button" class="flex items-center justify-center">
                            <UIcon name="i-ph-x-bold" class="size-3" />
                        </button>
                    </template>
                </USelectMenu>
                <UButton
                    label="Clear"
                    color="gray"
                    @click="clearFilters()"
                    class="text-center"
                />
                <UButton @click="overlays.create = true" color="black" icon="i-ph-plus-bold">
                    Add User
                </UButton>
            </div>
        </div>

        <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm">
            <UTable
                :rows="users"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-users-duotone', label: 'No users.' }"
                :ui="{ td: { padding: 'py-3' } }"
            >
                <template #role-data="{ row }">
                    <BadgeUserRole :role="row.role" />
                </template>
                <template #updated_at-data="{ row }">
                    <span>{{ dayjs(row.updated_at).format('lll') }}</span>
                </template>
                <template #actions-data="{ row }">
                    <UDropdown v-if="auth.user?.id !== row.id" :items="actions(row)">
                        <UButton color="gray" variant="ghost" icon="i-heroicons-ellipsis-horizontal-20-solid" />
                    </UDropdown>
                </template>
            </UTable>
        </div>

        <OverlayUserCreateUser v-model:open="overlays.create" @reload="fetchData" />
        <OverlayUserEditUser v-model:open="overlays.edit" @reload="fetchData" :user="selectedUser" />
        <OverlayUserRemoveUser v-model:open="overlays.remove" @reload="fetchData" :user="selectedUser" />
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import LocalizedFormat from 'dayjs/plugin/localizedFormat'
import { watchDebounced } from '@vueuse/core'
import { UserRole, type User } from '~/types/user'

dayjs.extend(LocalizedFormat)

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin] })
useHead({ title: 'Settings – Users' })

const auth = useAuthStore()
const client = useSupabaseClient()

const loading = ref(false)
const search = ref('')
const filters = ref<{ [key: string]: any }>({})
const users = ref<User[]>([])
const selectedUser = ref<User>()

const overlays = reactive({
    create: false,
    edit: false,
    remove: false
})

const columns = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'role', label: 'Role' },
    { key: 'updated_at', label: 'Updated' },
    { key: 'actions', class: 'w-px' },
]

const actions = (user: User) => [
    [
        {
            label: 'Edit',
            icon: 'i-ph-pencil-fill',
            click: () => {
                selectedUser.value = user
                overlays.edit = true
            },
        },
        {
            label: 'Remove',
            icon: 'i-ph-trash-fill',
            iconClass: 'text-red-400',
            click: () => {
                selectedUser.value = user
                overlays.remove = true
            },
        },
    ],
]

onMounted(() => fetchData())
watchDebounced(search, () => fetchData(), { debounce: 600 })
watch(filters, () => fetchData(), { deep: true })

async function fetchData() {
    loading.value = true

    let query = client.from('users')
        .select('id, name, email, role, metadata, updated_at')
        .is('deleted_at', null)
        .order('name')

    if (search.value) {
        query = query.or(`name.ilike.%${search.value}%, email.ilike.%${search.value}%`)
    }

    if (filters.value.role) {
        query = query.eq('role', filters.value.role.value)
    }

    const { data } = await query

    if (data) {
        users.value = data
    }
    
    loading.value = false
}

function clearFilters() {
    search.value = ''
    filters.value = {}
}
</script>
